import type { ReactNode } from "react";
import { useLocalization } from "@fluent/react";
import {
  Button,
  Row,
  Select,
  Alert,
  ResponsiveFlex,
} from "@narmi/design_system";
import { useIsACHDualApprovalRequired } from "byzantine/src/dbbl/businessLogic/entities/users/hooks";
import {
  REVERSAL_REASONS,
  REVERSAL_REASON_FLUENT_IDS,
  getReadableTimeFromCutoff,
  type ReversalReason,
} from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import { useInstitutionSettings } from "../../contexts/InstitutionSettingsContext";
import styles from "./reversalShared.module.scss";

export type ReversalReasonSelectProps = {
  reason: ReversalReason | "";
  onChange: (reason: ReversalReason) => void;
  errorText?: string;
};

// shared reason dropdown for the individual and bulk reverse flows
export const ReversalReasonSelect = ({
  reason,
  onChange,
  errorText,
}: ReversalReasonSelectProps) => {
  const { l10n } = useLocalization();

  return (
    <Select
      label={l10n.getString("reverse-payment-reason-label")}
      value={reason}
      onChange={(value: string) => onChange(value as ReversalReason)}
      errorText={errorText}
    >
      {REVERSAL_REASONS.map((reversalReason) => (
        <Select.Item key={reversalReason} value={reversalReason}>
          {l10n.getString(REVERSAL_REASON_FLUENT_IDS[reversalReason])}
        </Select.Item>
      ))}
    </Select>
  );
};

export const ReversalDetailRow = ({
  label,
  value,
}: {
  label: string;
  value: string | undefined | null;
}) => {
  if (!value) return null;

  return (
    <Row alignItems="center" className="margin--bottom--m">
      <Row.Item>
        <span className="fontColor--secondary">{label}</span>
      </Row.Item>
      <Row.Item shrink>
        <span className="fontColor--primary">{value}</span>
      </Row.Item>
    </Row>
  );
};

export type ReversalReceiptRow = {
  label: string;
  value: string | undefined | null;
};

const renderReceiptRow = ({ label, value }: ReversalReceiptRow) => {
  if (!value) return null;
  return (
    <Row key={label} alignItems="center">
      <Row.Item>
        <span className="fontSize--s fontColor--secondary">{label}</span>
      </Row.Item>
      <Row.Item shrink>
        <span className="fontSize--m fontColor--heading">{value}</span>
      </Row.Item>
    </Row>
  );
};

// title shown in the dialog header for a submitted reversal. switches to the
// "submitted for approval" copy when dual approval is required
export const useReversalReceiptTitle = (): ReactNode => {
  const { l10n } = useLocalization();
  const achDualApprovalRequired = useIsACHDualApprovalRequired();
  const titleText = achDualApprovalRequired
    ? l10n.getString("reverse-payment-receipt-dual-approval-title")
    : l10n.getString("reverse-payment-receipt-title");

  return (
    <span>
      {titleText}
      <i className="narmi-icon-check margin--left--xs fontColor--success" />
    </span>
  );
};

export const ReversalReceipt = ({ rows }: { rows: ReversalReceiptRow[] }) => {
  const { l10n } = useLocalization();
  const achDualApprovalRequired = useIsACHDualApprovalRequired();
  const { ach_next_cutoff: achNextCutoff } = useInstitutionSettings();
  const approvalDeadlineTime = getReadableTimeFromCutoff(achNextCutoff);
  const showApprovalDeadlineBanner =
    achDualApprovalRequired && Boolean(approvalDeadlineTime);

  return (
    <div>
      {showApprovalDeadlineBanner && (
        <div className="margin--bottom--l margin--top--l">
          <Alert kind="info" isActive isDismissable={false}>
            <span className="fontColor--primary fontSize--s">
              {l10n.getString("ach-payment-reversal-approval-deadline", {
                time: approvalDeadlineTime ?? "",
              })}
            </span>
          </Alert>
        </div>
      )}
      <div className={styles.receiptDetails}>
        <ResponsiveFlex gapSize="m">
          {rows.map(renderReceiptRow)}
        </ResponsiveFlex>
      </div>

      <p className="fontColor--primary fontSize--s">
        {l10n.getString("ach-payment-reversal-note")}
      </p>
    </div>
  );
};

export const ReversalReceiptFooter = ({
  handleClose,
  handleCopyPaymentLink,
}: {
  handleClose: () => void;
  handleCopyPaymentLink?: () => void;
}) => {
  const { l10n } = useLocalization();
  const achDualApprovalRequired = useIsACHDualApprovalRequired();

  return (
    <Row alignItems="center" justifyContent="end" gapSize="s">
      {achDualApprovalRequired && handleCopyPaymentLink && (
        <Row.Item shrink>
          <Button
            kind="secondary"
            startIcon="copy"
            onClick={handleCopyPaymentLink}
          >
            {l10n.getString("reverse-payment-receipt-copy-link")}
          </Button>
        </Row.Item>
      )}
      <Row.Item shrink>
        <Button onClick={handleClose}>{l10n.getString("label-done")}</Button>
      </Row.Item>
    </Row>
  );
};
